import { Building2, FileText, Layers, RefreshCw, ShieldAlert, Truck, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePageContextLabel } from '@/hooks/useAgentConversation'
import { PageContextEntity, type PageContextValue } from './pageContext'

const ENTITY_ICONS: Record<PageContextEntity, typeof Building2> = {
  [PageContextEntity.ORGANISATION]: Building2,
  [PageContextEntity.SALES_LEAD]: Truck,
  [PageContextEntity.INSURANCE_APPLICATION]: FileText,
  [PageContextEntity.CLAIM]: ShieldAlert,
  [PageContextEntity.POLICY_CHANGE]: RefreshCw,
  [PageContextEntity.MOTOR_CARRIER_DOT]: Truck,
}

interface PageContextSectionProps {
  context: PageContextValue | null
  /** False while the user has dismissed the chip for this page. */
  included: boolean
  onToggle: (included: boolean) => void
}

/**
 * What the agent will be told about the current page, shown above the composer
 * before anything is sent. Dismissing it drops the context from the next
 * message; navigating to another page brings it back.
 *
 * Shared by both panels: the caller passes the context its own route map
 * produced, and the label resolves against that agent's endpoint.
 */
export function PageContextSection({ context, included, onToggle }: PageContextSectionProps) {
  const { entity, isLoading } = usePageContextLabel(context)
  if (!context) return null

  const Icon = context.entityType ? ENTITY_ICONS[context.entityType] : Layers
  const filterCount = Object.keys(context.filters).length

  if (!included) {
    return (
      <div className="text-muted-foreground mb-1.5 flex items-center gap-2 text-xs">
        <span>Page context off</span>
        <Button variant="link" size="xs" className="h-auto p-0 text-xs" onClick={() => onToggle(true)}>
          Add {context.label}
        </Button>
      </div>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="bg-muted/40 mb-1.5 flex items-center gap-2 rounded-md border px-2.5 py-1.5">
          <Icon className="text-muted-foreground size-4 shrink-0" />
          <div className="min-w-0 flex-1 text-xs">
            <div className="truncate font-medium">
              {isLoading ? <Skeleton className="h-3.5 w-40" /> : (entity?.label ?? context.label)}
            </div>
            <div className="text-muted-foreground truncate">
              {[
                context.entityType ? context.label : null,
                entity?.sublabel || null,
                filterCount > 0 ? `${filterCount} filter${filterCount === 1 ? '' : 's'}` : null,
              ]
                .filter(Boolean)
                .join(' · ') || context.path}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label="Don't send page context"
            onClick={() => onToggle(false)}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </TooltipTrigger>
      {/* z-250: same as ConversationDropdown — rendered from inside the agent panel, which now sits above z-200. */}
      <TooltipContent className="z-250">Context sent with your message</TooltipContent>
    </Tooltip>
  )
}
